import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import { Spinner } from 'react-bootstrap'
import Loader from 'src/utils/Loader'
import {useFormik} from 'formik'
import {toast} from 'react-toastify'
import CommonConfirmationModal from 'src/utils/modals/ConfirmationModal'
import {useNavigate, useParams} from 'react-router-dom'
import { AppUrls } from 'src/app/routing/AppUrls'
import { validateOmsEditAccess, extractOrderPlatformId, isNativeCommerciumOrder } from '../orders/orderAccessUtils'
import {getInventoryVariantsListAsync, getOmsPlatformFieldByOwnerAsync} from 'src/redux/actions/omsPageActions'
import {
  getOmsOrderAsync,
  getOmsOrderCustomFieldsAsync,
  saveOmsOrderCustomFieldsAsync,
  updateOmsOrderAsync,
  createOmsOrderInvoiceAsync,
  downloadOmsOrderInvoiceAsync,
  updateOmsOrderAllocationAsync,
} from 'src/redux/actions/omsOrderActions'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {buildCreateOrderPayload} from '../oms-create-order/buildCreateOrderPayload'
import {AddressState, CreateOrderFormSnapshot, AllocationDetails, AllocationHistory, OrderDiscountMode} from '../oms-create-order/types'
import OrderCustomFields from '../oms-create-order/OrderCustomFields'
import OmsAddressBlock from '../oms-create-order/OmsAddressBlock'
import OmsPlatformFieldDataSelect from '../oms-create-order/OmsPlatformFieldDataSelect'
import {OmsTooltip, OmsInfoLabel, OmsInfoText} from '../oms-create-order/OmsTooltip'
import {
  extractPlatformFieldsList,
  findPlatformFieldByCode,
  isEmptyOrderFieldValue,
  OMS_ORDER_PLATFORM_FIELD_OWNER,
  pickDraftOrderStatusOption,
  pickFirstPlatformFieldOption,
  pickManualOrderChannelOption,
  useDefaultOmsPlatformFieldOption,
} from '../oms-create-order/platformFieldHelpers'
import {
  hydrateOrderConfigFromOrderData,
  getOrderDataSeedOption,
  ORDER_DATA_TYPES,
  OrderDataRow,
  validateRequiredCustomFields,
  validateCustomFieldFormats,
  processDuplicateLineItem,
} from '../oms-create-order/orderDataHelpers'
import {PlatformFieldDataSelectOption} from '../oms-create-order/types'
import {buildCustomFieldsPayloadForEdit} from 'src/marketConnect/helpers/crud-helper/helpers'
import ManagePackagesModal from '../orders/components/ManagePackagesModal'
import ManageShipmentsModal from '../orders/components/ManageShipmentsModal'
import { determineShippingRoute, ShippingRoute } from '../orders/components/determineShippingRoute'
import AllocationPill from '../orders/components/AllocationPill'
import {
  computeLineItemInventoryStatus,
  fetchStockForLineItems,
  shouldEnforceInventoryOnSave,
  validateInventoryForSave,
  InventoryStatus,
} from './inventoryValidationUtils'
import '../oms-create-order/oms-create-order.css'
import '../oms-create-order/oms-order.css'
import PremiumBreadcrumb from 'src/app/components/navigation/PremiumBreadcrumb'
import {getCurrencySymbol} from 'src/app/pages/dashboard/utils/parseDashboardReportResponse'

// ─── Types ────────────────────────────────────────────────────────────────────

interface VariantOption {
  value: number
  label: string
  sku: string
  platform_product: number
  price: number
  availableQty?: number
}

interface LineItem {
  id: string
  isNew: boolean
  variantOption: VariantOption | null
  variantSearch: string
  variantPage: number
  variantHasMore: boolean
  variantOptions: VariantOption[]
  variantLoading: boolean
  name: string
  sku: string
  platform_product: number | null
  platform_product_variant: number | null
  price: number | string
  quantity: number | string
  discount: number | string
  allocation_details?: AllocationDetails
  allocation_history?: AllocationHistory[]
  inventoryStatus?: InventoryStatus
}

// ─── Shared class constants ───────────────────────────────────────────────────

const inputCls = 'oms-input'
const inputSmCls = 'oms-input oms-input--sm'
const inputDisabledCls = 'oms-input'
const sectionCardCls = 'oms-card'
const sectionBodyCls = 'oms-card__body'

const thCls = 'oms-th text-left'
const tdCls = 'oms-td'

// ─── Factories ────────────────────────────────────────────────────────────────

const emptyAddress = (): AddressState => ({addr1: '', addr2: '', city: '', state: '', zip: '', country: ''})

const newLineItemRow = (): LineItem => ({
  id: `item-${Date.now()}-${Math.random()}`,
  isNew: true,
  variantOption: null,
  variantSearch: '',
  variantPage: 1,
  variantHasMore: true,
  variantOptions: [],
  variantLoading: false,
  name: '',
  sku: '',
  platform_product: null,
  platform_product_variant: null,
  price: 0,
  quantity: 1,
  discount: 0,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractPrice = (p: any): number => {
  if (p == null) return 0
  if (typeof p === 'number') return p
  return Number(p?.value ?? 0) || 0
}

const mapAddressFromApi = (addr: any): AddressState => ({
  addr1: addr?.address_1 || '',
  addr2: addr?.address_2 || '',
  city: addr?.city || '',
  state: addr?.state || '',
  zip: addr?.zip_code || addr?.zip || '',
  country: addr?.country || '',
})

const formatDate = (raw: string): string => {
  try {
    return new Date(raw).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return raw
  }
}

// ─── Status badge helpers ─────────────────────────────────────────────────────

const getFulfillmentBadge = (status: string) => {
  if (status === 'Fulfilled') {
    return <span className='inline-block px-3 py-[6px] bg-[#d4edda] text-[#155724] rounded-[4px] font-semibold text-[13px] w-full box-border'>Fulfilled</span>
  }
  return <span className='inline-block px-3 py-[6px] bg-[#fff3cd] text-[#856404] rounded-[4px] font-semibold text-[13px] w-full box-border'>UnFulfilled</span>
}

const getAllocationBadge = (status: string) => {
  const base = 'inline-block px-3 py-[6px] rounded-[6px] font-semibold text-[12px] w-full box-border text-white text-center'
  if (status === 'Fully Allocated' || status === '✔ Fully Reserved' || status === 'ALLOCATED') {
    return <span className={base} style={{ backgroundColor: '#16A34A' }}>✔ Fully Allocated</span>
  }
  if (status === 'Partially Allocated') {
    return <span className={base} style={{ backgroundColor: '#D97706' }}>⚡ Partially Allocated</span>
  }
  if (status === 'Deallocated') {
    return <span className={base} style={{ backgroundColor: '#64748B' }}>Deallocated</span>
  }
  return <span className={base} style={{ backgroundColor: '#DC2626' }}>✗ Not Allocated</span>
}

const getStockBadgeStyle = (availableQty: number) => {
  if (availableQty <= 0) return { bg: '#DC2626', border: '#DC2626', color: '#ffffff' }
  if (availableQty <= 10) return { bg: '#D97706', border: '#D97706', color: '#ffffff' }
  return { bg: '#16A34A', border: '#16A34A', color: '#ffffff' }
}

const checkHasInvoice = (order: any) => {
  if (!order || !order.invoices) return false
  if (Array.isArray(order.invoices)) return order.invoices.length > 0
  return Object.keys(order.invoices).length > 0
}

// ─── Component ────────────────────────────────────────────────────────────────

const EditOmsOrder: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {orderId} = useParams<{orderId: string}>()
  const {saving: orderSaving} = useAppSelector((state) => state.omsOrder)
  const [saveCooldown, setSaveCooldown] = useState(false)
  const [downloadCooldown, setDownloadCooldown] = useState(false)
  const omsStoreData = useAppSelector((state) => state.fetchConnectedPlatform?.omsStoreData ?? null)
  const omsPlatformId = omsStoreData?.id ?? null

  // ── Page meta ────────────────────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true)
  const [orderCreatedAt, setOrderCreatedAt] = useState('')
  const [fulfillmentStatus, setFulfillmentStatus] = useState('UnFulfilled')
  const [apiAllocationStatus, setApiAllocationStatus] = useState<string | null>(null)
  const [isCreatedByCommercium, setIsCreatedByCommercium] = useState<boolean>(true)
  const [packages, setPackages] = useState<any[]>([])
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [hasInvoice, setHasInvoice] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelingOrder, setCancelingOrder] = useState(false)
  const [rawOrder, setRawOrder] = useState<any>(null)

  // ── Logistics Modals ──────────────────────────────────────────────────────
  const [showPackageModal, setShowPackageModal] = useState(false)
  const [showShipmentModal, setShowShipmentModal] = useState(false)
  const [shippingRoute, setShippingRoute] = useState<ShippingRoute | 'loading'>('loading')
  const [initialOpenPkgId, setInitialOpenPkgId] = useState<number | null>(null)
  const [initialOpenShipmentId, setInitialOpenShipmentId] = useState<number | null>(null)
  const [forceViewMode, setForceViewMode] = useState(false)

  // ── Customer (locked display) ─────────────────────────────────────────────
  const [customerLabel, setCustomerLabel] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | string | null>(null)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [billing, setBilling] = useState<AddressState>(emptyAddress())
  const [shipping, setShipping] = useState<AddressState>(emptyAddress())

  // ── Order config ──────────────────────────────────────────────────────────
  const [salesOrderNumber, setSalesOrderNumber] = useState<string | null>(null)
  const [channelSource, setChannelSource] = useState('')
  const [orderStatus, setOrderStatus] = useState('')
  const [orderStatusLabel, setOrderStatusLabel] = useState<string | null>(null)
  const [orderStatusApiId, setOrderStatusApiId] = useState<string | null>(null)
  // Raw lifecycle status directly from api_status — used exclusively by the permissions engine.
  // Kept separate from `orderStatus` (which holds the platform field dropdown value).
  const [apiLifecycleStatus, setApiLifecycleStatus] = useState<string>('')
  const [shippingMethod, setShippingMethod] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [shipmentDate, setShipmentDate] = useState('')
  const [orderConfigFields, setOrderConfigFields] = useState<any[]>([])
  const [orderConfigFieldsLoading, setOrderConfigFieldsLoading] = useState(false)
  const [orderDataRows, setOrderDataRows] = useState<OrderDataRow[]>([])
  const [orderDataFieldIds, setOrderDataFieldIds] = useState<{
    channel: number | null
    shipping: number | null
    payment: number | null
    orderStatus: number | null
    location: number | null
    taxCode: number | null
  }>({
    channel: null,
    shipping: null,
    payment: null,
    orderStatus: null,
    location: null,
    taxCode: null,
  })

  // ── Warehouse (LOCATION platform field) ─────────────────────────────────
  const [warehouseId, setWarehouseId] = useState('')
  const [fieldSeedOptions, setFieldSeedOptions] = useState<
    Partial<Record<'channel' | 'orderStatus' | 'shipping' | 'payment' | 'location' | 'taxCode', PlatformFieldDataSelectOption | null>>
  >({})

  // ── Line items ────────────────────────────────────────────────────────────
  const variantTimers = useRef<Record<string, NodeJS.Timeout>>({})
  const [openVariantRow, setOpenVariantRow] = useState<string | null>(null)
  const [glowingRowId, setGlowingRowId] = useState<string | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{top: number; left: number; width: number} | null>(null)
  const activeVariantTriggerRef = useRef<HTMLElement | null>(null)
  const dropdownPanelRef = useRef<HTMLDivElement | null>(null)
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [isRefetchingInventory, setIsRefetchingInventory] = useState(false)

  // ── Totals ────────────────────────────────────────────────────────────────
  const [shippingFee, setShippingFee] = useState<number | string>(0)
  const [taxCode, setTaxCode] = useState('')
  const [taxPercent, setTaxPercent] = useState<number | null>(null)
  const [taxPrice, setTaxPrice] = useState(0)
  const [amountPaid, setAmountPaid] = useState<number | string>(0)
  const [apiGrandTotal, setApiGrandTotal] = useState<number>(0)
  const [paymentRef, setPaymentRef] = useState('')
  const [orderDiscountMode, setOrderDiscountMode] = useState<OrderDiscountMode>('amount')
  const [orderDiscountValue, setOrderDiscountValue] = useState<number | string>('')
  const [orderCurrency, setOrderCurrency] = useState('USD')
  const currencySymbol = getCurrencySymbol(orderCurrency)

  // ── Custom fields ─────────────────────────────────────────────────────────
  const [orderCustomFieldsRaw, setOrderCustomFieldsRaw] = useState<any[]>([])
  const customFieldsFormik = useFormik<{custom_fields: Record<number, any>}>({
    initialValues: {custom_fields: {}},
    onSubmit: () => {},
  })
  const [orderFieldsMeta, setOrderFieldsMeta] = useState<any[]>([])

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [internalComments, setInternalComments] = useState('')
  const [customerNotes, setCustomerNotes] = useState('')

  // ── Computed totals ───────────────────────────────────────────────────────
  const itemsSubtotal = lineItems.reduce((sum, item) => sum + Math.max(0, (Number(item.quantity) || 0) * (Number(item.price) || 0) - (Number(item.discount) || 0)), 0)
  const shippingNumeric = Number(shippingFee) || 0
  const orderDiscountNumeric = orderDiscountValue === '' ? 0 : Number(orderDiscountValue) || 0
  const orderDiscountBase = itemsSubtotal + shippingNumeric
  const orderDiscountAmount =
    orderDiscountNumeric <= 0
      ? 0
      : orderDiscountMode === 'percent'
        ? orderDiscountBase * (orderDiscountNumeric / 100)
        : orderDiscountNumeric
  const calculatedTax = taxPrice
  const computedGrandTotal = Math.max(0, orderDiscountBase - orderDiscountAmount + calculatedTax)
  const grandTotal = (computedGrandTotal > 0 || lineItems.length > 0) ? computedGrandTotal : apiGrandTotal

  useEffect(() => {
    if (isCreatedByCommercium === false) return
    if (taxPercent != null) {
      setTaxPrice((itemsSubtotal + (Number(shippingFee) || 0)) * (taxPercent / 100))
    } else {
      setTaxPrice(0)
    }
  }, [itemsSubtotal, shippingFee, taxPercent, isCreatedByCommercium])

  useEffect(() => {
    if ((Number(amountPaid) || 0) > grandTotal) {
      setAmountPaid(grandTotal)
    }
  }, [grandTotal, amountPaid])

  const balanceDue = Math.max(0, grandTotal - (Number(amountPaid) || 0))
  const enforceLineItemInventory = shouldEnforceInventoryOnSave(orderStatusApiId)

  const resolveOrderFieldId = (
    savedFieldId: number | null,
    fieldCode: string,
    options?: {inputType?: string}
  ): number | null =>
    savedFieldId ??
    findPlatformFieldByCode(orderConfigFields, fieldCode, options)?.id ??
    null

  // ── Load order + custom fields ────────────────────────────────────────────
  const fetchOrderDetails = useCallback(async (isSilent = false) => {
    if (!orderId) {
      setPageLoading(false)
      return
    }
    if (!isSilent) setPageLoading(true)
    if (omsPlatformId) setOrderConfigFieldsLoading(true)
    try {
      const shippingRoutePromise = determineShippingRoute(Number(orderId), dispatch)
      
      // 1. Fetch order and custom fields first
      const coreRequests: Promise<any>[] = [
        dispatch(getOmsOrderAsync({orderId, query: {show_decrypted: 1}})).unwrap(),
        dispatch(getOmsOrderCustomFieldsAsync({orderId})).unwrap(),
      ]
      
      const coreResults = await Promise.all(coreRequests)
      const orderRes = coreResults[0]
      const cfRes = coreResults[1]
      const order = orderRes?.data ?? orderRes
      setRawOrder(order)

      // 2. Validate access and self-heal the platform ID if Redux was wiped
      const activePlatformId = omsPlatformId || extractOrderPlatformId(order)
      
      const { isAccessGranted, isCreated } = validateOmsEditAccess(order, activePlatformId)
      if (!isAccessGranted) {
        toast.error("Invalid Order: Not a valid Commercium created or synced order.")
        navigate(AppUrls.allOrders())
        return
      }

      // 3. Fetch Platform Fields using the guaranteed active platform ID
      let platformFieldsResOrder = null
      let platformFieldsResShipment = null
      
      if (activePlatformId) {
        setOrderConfigFieldsLoading(true)
        const fieldRequests = [
          dispatch(getOmsPlatformFieldByOwnerAsync({ platformId: activePlatformId, owner: OMS_ORDER_PLATFORM_FIELD_OWNER })).unwrap(),
          dispatch(getOmsPlatformFieldByOwnerAsync({ platformId: activePlatformId, owner: 'SHIPMENT' })).unwrap()
        ]
        const fieldResults = await Promise.all(fieldRequests)
        platformFieldsResOrder = fieldResults[0]
        platformFieldsResShipment = fieldResults[1]
      }

      const platformFields = [
        ...(platformFieldsResOrder ? extractPlatformFieldsList(platformFieldsResOrder) : []),
        ...(platformFieldsResShipment ? extractPlatformFieldsList(platformFieldsResShipment) : []),
      ]

      if (platformFields.length) {
        setOrderConfigFields(platformFields)
      }

      const hydratedConfig = hydrateOrderConfigFromOrderData(order?.order_data)
      setOrderDataRows(hydratedConfig.rows)
      setOrderDataFieldIds(hydratedConfig.fieldIds)

      if (hydratedConfig.channelSource) setChannelSource(hydratedConfig.channelSource)
      if (hydratedConfig.shippingMethod) setShippingMethod(hydratedConfig.shippingMethod)
      if (hydratedConfig.paymentMethod) setPaymentMethod(hydratedConfig.paymentMethod)
      if (hydratedConfig.orderStatus) {
        setOrderStatus(hydratedConfig.orderStatus)
      } else if (order?.api_status) {
        setOrderStatus(order.api_status)
      }
      // Always set the raw lifecycle status from api_status — this is the single reliable
      // source used by the permissions engine to detect CANCELLED / COMPLETED orders.
      // We read from multiple fields and normalize to uppercase to be resilient against
      // backend field name or casing variations.
      const rawLifecycle =
        order?.api_status ??
        order?.status ??
        order?.order_status ??
        order?.lifecycle_status ??
        null
      const normalizedLifecycle = rawLifecycle ? String(rawLifecycle).toUpperCase().trim() : ''
      setApiLifecycleStatus(normalizedLifecycle)
      setOrderStatusLabel(normalizedLifecycle || order?.api_status || '')
      setOrderStatusApiId(normalizedLifecycle || order?.api_status || '')
      if (hydratedConfig.taxCode) setTaxCode(hydratedConfig.taxCode)
      const resolvedWarehouseId =
        hydratedConfig.location || (order?.warehouse_id != null ? String(order.warehouse_id) : '')
      if (resolvedWarehouseId) setWarehouseId(resolvedWarehouseId)

      setFieldSeedOptions({
        channel: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.CHANNEL),
        orderStatus: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.ORDER_STATUS),
        shipping: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.SHIPPINGPROVIDER),
        payment: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.PAYMENT_METHOD),
        location: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.LOCATION),
        taxCode: getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.TAXCODE),
      })

      if (order?.tax_percent != null && order?.tax_percent !== '') {
        setTaxPercent(Number(order.tax_percent))
      } else if (order?.tax_rate != null && order?.tax_rate !== '') {
        setTaxPercent(Number(order.tax_rate))
      } else {
        const taxCodeSeed = getOrderDataSeedOption(order?.order_data, ORDER_DATA_TYPES.TAXCODE)
        if (taxCodeSeed && taxCodeSeed.label) {
          const match = taxCodeSeed.label.match(/\((\d+(?:\.\d+)?)%\)/)
          if (match) setTaxPercent(parseFloat(match[1]))
        }
      }

        // Order number
        setSalesOrderNumber(order?.api_code || order?.order_number || order?.api_id || String(orderId))

        // Created date
        const rawDate = order?.api_created_at || order?.created_at
        if (rawDate) setOrderCreatedAt(formatDate(rawDate))

        // Customer
        const cust = order?.customer
        if (cust) {
          const fullName = [cust.fname || cust.first_name, cust.lname || cust.last_name]
            .filter(Boolean)
            .join(' ')
            .trim()
          const name = cust.display_name || cust.name || fullName || cust.user_name || cust.email || ''
          setCustomerLabel(`${name}${cust.id ? ` (ID: ${cust.id})` : ''}`)
          setSelectedCustomerId(cust.id || order?.customer_id || null)
          setEmail(cust.email || cust.user_name || '')
          setPhone(cust.phone || cust.phone_number || '')
        } else if (order?.customer_id) {
          setCustomerLabel(`Customer ID: ${order.customer_id}`)
          setSelectedCustomerId(order.customer_id)
        }

        // Addresses — prefer top-level objects; fall through to customer nested
        const billingRaw = typeof order?.billing_address === 'object' ? order.billing_address
          : typeof cust?.billing_address === 'object' ? cust.billing_address : null
        if (billingRaw) setBilling(mapAddressFromApi(billingRaw))

        const shippingRaw = typeof order?.shipping_address === 'object' ? order.shipping_address
          : typeof cust?.shipping_address === 'object' ? cust.shipping_address : null
        if (shippingRaw) setShipping(mapAddressFromApi(shippingRaw))

        // Notes and missing hydrated fields
        if (order?.order_notes) setInternalComments(order.order_notes)
        if (order?.customer_notes) setCustomerNotes(order.customer_notes)
        if (order?.shipping_date) setShipmentDate(String(order.shipping_date).substring(0, 10))
        
        const firstPayment = order?.payments?.[0]
        if (firstPayment?.api_id) {
          setPaymentRef(firstPayment.api_id)
        } else if (order?.payment_reference) {
          setPaymentRef(order.payment_reference)
        }

        // Fulfillment + allocation badges
        setFulfillmentStatus(order?.isFulfilled ? 'Fulfilled' : 'UnFulfilled')
        setApiAllocationStatus(order?.api_id2 || null)
        // Using the strictly evaluated flag from our route access check
        setIsCreatedByCommercium(isCreated)
        setHasInvoice(checkHasInvoice(order))

        // Fallback shipping when order_data has no SHIPPINGPROVIDER row
        if (!hydratedConfig.shippingMethod && order?.shipping_method) {
          setShippingMethod(String(order.shipping_method))
        }

        // Line items
        const rawItems: any[] = Array.isArray(order?.line_items) ? order.line_items : []
        const parsedItems = rawItems.map((li: any) => {
          const variantId = li.platform_product_variant?.id ?? li.platform_product_variant ?? null
          const productId = li.platform_product?.id ?? li.platform_product ?? null
          const price = extractPrice(li.price)
          return {
            id: `item-${li.id ?? Date.now()}-${Math.random()}`,
            isNew: false,
            variantOption: {
              value: variantId,
              label: li.name || li.sku || `Variant ${variantId}`,
              sku: li.sku || '',
              platform_product: productId,
              price,
              availableQty: li.platform_product_variant?.quantity != null ? li.platform_product_variant.quantity : undefined,
            },
            variantSearch: '',
            variantPage: 1,
            variantHasMore: true,
            variantOptions: [],
            variantLoading: false,
            name: li.name || '',
            sku: li.sku || '',
            platform_product: productId,
            platform_product_variant: variantId,
            price,
            quantity: li.quantity ?? 1,
            discount: extractPrice(li.discount_price),
            allocation_details: li.allocation_details,
            allocation_history: li.allocation_history,
          }
        })
        setLineItems(parsedItems)

        if (resolvedWarehouseId && parsedItems.length > 0) {
          fetchStockForLineItems(dispatch, parsedItems, resolvedWarehouseId).then((updatedItems) => {
            setLineItems((prev) =>
              prev.map((p) => {
                const up = updatedItems.find((u: any) => u.id === p.id)
                return up ? { ...p, variantOption: up.variantOption, inventoryStatus: up.inventoryStatus } : p
              })
            )
          })
        }

        // Totals
        setShippingFee(extractPrice(order?.shipping_price))
        setTaxPrice(extractPrice(order?.tax_price))
        setApiGrandTotal(extractPrice(order?.total_price?.value ?? order?.total_price))
        setOrderCurrency(
          order?.order_currency ||
          order?.total_price?.currency_iso ||
          order?.shipping_price?.currency_iso ||
          omsStoreData?.currency_iso ||
          'USD'
        )

        const orderDiscountPercent =
          order?.discount_percent != null && order?.discount_percent !== ''
            ? Number(order?.discount_percent)
            : NaN
        if (Number.isFinite(orderDiscountPercent) && orderDiscountPercent > 0) {
          setOrderDiscountMode('percent')
          setOrderDiscountValue(orderDiscountPercent)
        } else {
          const orderDiscountAmountFromApi = extractPrice(order?.discount_price?.value ?? order?.discount_price)
          if (orderDiscountAmountFromApi > 0) {
            setOrderDiscountMode('amount')
            setOrderDiscountValue(orderDiscountAmountFromApi)
          } else {
            setOrderDiscountMode('amount')
            setOrderDiscountValue('')
          }
        }

        const firstPaymentObj = order?.payments?.[0]
        setAmountPaid(firstPaymentObj ? (Number(firstPaymentObj.paid_amount) || 0) : 0)

        // Packages / shipments
        if (Array.isArray(order?.shipments) && order.shipments.length) {
          setPackages(order.shipments)
        } else if (Array.isArray(order?.packages) && order.packages.length) {
          setPackages(order.packages)
        }

      // Custom fields
      const cfRows = cfRes?.results?.data ?? cfRes?.data?.results?.data ?? []
      setOrderCustomFieldsRaw(cfRows)

      const route = await shippingRoutePromise
      setShippingRoute(route)
    } catch (err: any) {
      console.error('Failed to load order', err)
      if (!isSilent) {
        toast.error(err?.data?.message || err?.message || 'Failed to load order. It may have been deleted or not found.')
        navigate(AppUrls.allOrders())
      }
    } finally {
      setOrderConfigFieldsLoading(false)
      if (!isSilent) setPageLoading(false)
    }
  }, [dispatch, omsPlatformId, omsStoreData?.currency_iso, orderId])

  useEffect(() => {
    fetchOrderDetails()
  }, [fetchOrderDetails])

  // ── Outside-click: variant row dropdowns ─────────────────────────────────
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Element
      if (!target.closest('[data-variant-dropdown]')) {
        setOpenVariantRow(null)
        setDropdownCoords(null)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  const handleWarehouseChange = async (val: string) => {
    setWarehouseId(val)
    if (!val) return
    setIsRefetchingInventory(true)
    try {
      const currentItems = lineItems.map((item) => {
        if (!item.isNew || item.platform_product_variant) return item
        return {
          ...item,
          variantOption: null,
          name: '',
          sku: '',
          platform_product: null,
          platform_product_variant: null,
          variantOptions: [],
          variantSearch: '',
          variantPage: 1,
          variantHasMore: true,
          inventoryStatus: undefined
        }
      })
      const updatedItems = await fetchStockForLineItems(dispatch, currentItems, val)
      setLineItems(updatedItems)
    } finally {
      setIsRefetchingInventory(false)
    }
  }

  useDefaultOmsPlatformFieldOption({
    fieldId: resolveOrderFieldId(orderDataFieldIds.orderStatus, 'ORDER_STATUS'),
    enabled: !pageLoading && !orderConfigFieldsLoading && isEmptyOrderFieldValue(orderStatus),
    pick: pickDraftOrderStatusOption,
    onApply: (option) => {
      setOrderStatus(option.value)
      setOrderStatusLabel(option.label)
      setOrderStatusApiId(option.raw?.api_id ?? null)
      setFieldSeedOptions((prev) => ({...prev, orderStatus: option}))
    },
  })

  useDefaultOmsPlatformFieldOption({
    fieldId: resolveOrderFieldId(orderDataFieldIds.channel, 'CHANNEL'),
    enabled:
      !pageLoading &&
      !orderConfigFieldsLoading &&
      isCreatedByCommercium === true &&
      isEmptyOrderFieldValue(channelSource),
    pick: pickManualOrderChannelOption,
    onApply: (option) => {
      setChannelSource(option.value)
      setFieldSeedOptions((prev) => ({...prev, channel: option}))
    },
  })

  useDefaultOmsPlatformFieldOption({
    fieldId: resolveOrderFieldId(orderDataFieldIds.location, 'LOCATION'),
    enabled: !pageLoading && !orderConfigFieldsLoading && isEmptyOrderFieldValue(warehouseId),
    pick: pickFirstPlatformFieldOption,
    onApply: (option) => {
      setFieldSeedOptions((prev) => ({...prev, location: option}))
      void handleWarehouseChange(option.value)
    },
  })

  // ── Variant fetch (per new row) ───────────────────────────────────────────
  const fetchVariantOptions = useCallback(
    async (itemId: string, page: number, search: string) => {
      if (!warehouseId) return
      setLineItems((prev) =>
        prev.map((item) => (item.id === itemId ? {...item, variantLoading: true} : item))
      )
      try {
        const res: any = await dispatch(
          getInventoryVariantsListAsync({
            page,
            limit: 10,
            search: search.trim() || undefined,
            sort_by: 'asc',
            order_by: 'id',
            source_warehouse_id: warehouseId,
            destination_warehouse_id: warehouseId,
          })
        )
        const data: any[] = res?.payload?.results?.data || []
        const hasMore = !!res?.payload?.next
        const newOptions: VariantOption[] = data.map((it: any) => ({
          value: it.id,
          label: it.name || it.sku || `Variant ${it.id}`,
          sku: it.sku || '',
          platform_product: it.platform_product_id ?? 0,
          price: it.price ?? 0,
          availableQty: it.source_total_quantity ?? it.total_quantity ?? 0,
        }))
        setLineItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item
            return {
              ...item,
              variantOptions: page === 1 ? newOptions : [...item.variantOptions, ...newOptions],
              variantPage: page,
              variantHasMore: hasMore,
              variantLoading: false,
            }
          })
        )
      } catch {
        setLineItems((prev) =>
          prev.map((item) => (item.id === itemId ? {...item, variantLoading: false} : item))
        )
      }
    },
    [dispatch, warehouseId]
  )

  const updateVariantDropdownPosition = useCallback(() => {
    if (!activeVariantTriggerRef.current) return
    const rect = activeVariantTriggerRef.current.getBoundingClientRect()
    let top = rect.bottom + 4
    const left = rect.left
    const width = rect.width
    if (top + 250 > window.innerHeight) {
      top = rect.top - 250 - 4
    }
    if (dropdownPanelRef.current) {
      dropdownPanelRef.current.style.top = `${top}px`
      dropdownPanelRef.current.style.left = `${left}px`
      dropdownPanelRef.current.style.width = `${width}px`
    }
    setDropdownCoords({ top, left, width })
  }, [])

  useEffect(() => {
    if (openVariantRow) {
      const handleScroll = (e: Event) => {
        const target = e.target as Element
        if (dropdownPanelRef.current && dropdownPanelRef.current.contains(target)) return
        setOpenVariantRow(null)
      }
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [openVariantRow])

  useEffect(() => {
    if (!openVariantRow) {
      setDropdownCoords(null)
      activeVariantTriggerRef.current = null
    }
  }, [openVariantRow])

  const openVariantDropdown = (itemId: string, triggerElement: HTMLElement) => {
    setOpenVariantRow(itemId)
    activeVariantTriggerRef.current = triggerElement
    updateVariantDropdownPosition()
    const item = lineItems.find((i) => i.id === itemId)
    if (item && item.variantOptions.length === 0 && warehouseId) {
      fetchVariantOptions(itemId, 1, item.variantSearch)
    }
  }

  const handleVariantSearch = (itemId: string, search: string) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === itemId ? {...item, variantSearch: search, variantPage: 1} : item))
    )
    if (variantTimers.current[itemId]) clearTimeout(variantTimers.current[itemId])
    variantTimers.current[itemId] = setTimeout(() => fetchVariantOptions(itemId, 1, search), 300)
  }

  const handleVariantScroll = (e: React.UIEvent<HTMLDivElement>, item: LineItem) => {
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50 && item.variantHasMore && !item.variantLoading) {
      fetchVariantOptions(item.id, item.variantPage + 1, item.variantSearch)
    }
  }

  const selectVariant = (itemId: string, opt: VariantOption) => {
    if (processDuplicateLineItem(lineItems, opt, itemId, setGlowingRowId, setOpenVariantRow, setLineItems)) return

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item

        return {
          ...item,
          variantOption: opt,
          name: opt.label,
          sku: opt.sku,
          platform_product: opt.platform_product,
          platform_product_variant: opt.value,
          price: opt.price || item.price,
          variantSearch: '',
          variantOptions: [],
          variantPage: 1,
          variantHasMore: true,
          inventoryStatus: computeLineItemInventoryStatus(opt.availableQty, item.quantity),
        }
      })
    )
    setOpenVariantRow(null)
  }

  // ── Line item handlers ────────────────────────────────────────────────────
  const addLineItem = () => setLineItems((prev) => [...prev, newLineItemRow()])

  const updateLineItem = (
    id: string,
    field: keyof Pick<LineItem, 'quantity' | 'price' | 'discount'>,
    value: number | string
  ) => setLineItems((prev) => prev.map((item) => {
    if (item.id !== id) return item
    const updated = { ...item, [field]: value }
    if (field === 'quantity' && updated.variantOption?.availableQty != null) {
      updated.inventoryStatus = computeLineItemInventoryStatus(
        updated.variantOption.availableQty,
        value
      )
    }
    return updated
  }))

  const removeLineItem = (id: string) => setLineItems((prev) => prev.filter((item) => item.id !== id))

  const getLineSubtotal = (item: LineItem) => Math.max(0, (Number(item.quantity) || 0) * (Number(item.price) || 0) - (Number(item.discount) || 0))

  const buildCurrentFormSnapshot = (overrides?: Partial<CreateOrderFormSnapshot>): CreateOrderFormSnapshot => {
    return {
      customerId: selectedCustomerId,
      firstName: '',
      lastName: '',
      displayName: '',
      email,
      phone,
      billing,
      shipping,
      warehouseId,
      lineItems: lineItems.map((item) => ({
        platform_product: item.platform_product,
        platform_product_variant: item.platform_product_variant,
        name: item.name,
        sku: item.sku,
        quantity: Number(item.quantity) || 0,
        price: Number(item.price) || 0,
        discount: Number(item.discount) || 0,
      })),
      calculatedTax,
      grandTotal,
      shippingFee: Number(shippingFee) || 0,
      shippingMethod,
      channelSource,
      paymentMethod,
      orderStatus,
      orderStatusLabel,
      orderStatusApiId,
      amountPaid: Number(amountPaid) || 0,
      paymentRef,
      shipmentDate,
      customerNotes,
      orderNotes: internalComments,
      taxCode,
      taxPercent,
      orderCurrency,
      dutyPrice: 0,
      orderDiscountMode,
      orderDiscountValue: orderDiscountValue === '' ? '' : Number(orderDiscountValue) || 0,
      priceListId: '',
      ...overrides,
    }
  }

  // ── Save handler ──────────────────────────────────────────────────────────
  const handleSaveOrder = async () => {
    if (saveCooldown) return
    setSaveCooldown(true)
    setTimeout(() => setSaveCooldown(false), 5000)
    if (!orderId) return
    if (!salesOrderNumber?.trim()) {
      toast.error('Order number is missing.')
      return
    }
    if (!selectedCustomerId) {
      toast.error('Customer is missing.')
      return
    }
    if (!warehouseId) {
      toast.error('Please select a warehouse.')
      return
    }
    if (isCreatedByCommercium && !channelSource) {
      toast.error('Please select a channel source.')
      return
    }
    if (isCreatedByCommercium && !orderStatus) {
      toast.error('Please select an order status.')
      return
    }

    const validItems = lineItems.filter(
      (i) => i.platform_product && i.platform_product_variant && Number(i.quantity) > 0
    )
    if (!validItems.length) {
      toast.error('Add at least one line item with a product variant and quantity.')
      return
    }

    const missingPrice = lineItems.some(
      (item) => item.platform_product && (item.price === null || item.price === undefined || item.price === '' as any)
    )
    if (missingPrice) {
      toast.error('Each line item must have a valid Unit Price.')
      return
    }

    const inventoryError = validateInventoryForSave(lineItems, orderStatusApiId, orderStatusLabel)
    if (inventoryError) {
      toast.error(inventoryError)
      return
    }

    if (!isNativeCommerciumOrder(rawOrder)) {
      if (!warehouseId) {
        toast.error('Please select a warehouse.')
        return
      }

      try {
        await dispatch(
          updateOmsOrderAllocationAsync({
            orderId,
            data: {
              api_status: 'ALLOCATED',
              warehouse_id: Number(warehouseId)
            }
          })
        ).unwrap()
        
        toast.success('Allocation updated successfully.')
        await fetchOrderDetails(true)
      } catch (err: any) {
        console.error('Allocation update failed', err)
        toast.error(err?.data?.message || err?.message || 'Failed to update allocation.')
      }
      return
    }

    const snapshot = buildCurrentFormSnapshot()

    if (isCreatedByCommercium) {
      const cfErrors = validateRequiredCustomFields(customFieldsFormik.values.custom_fields, orderFieldsMeta)
      if (cfErrors.length > 0) {
        toast.error(cfErrors[0])
        return
      }
    }

    const formatErrors = validateCustomFieldFormats(customFieldsFormik.values.custom_fields, orderFieldsMeta)
    if (formatErrors.length > 0) {
      toast.error(formatErrors[0])
      return
    }

    try {
      await dispatch(
        updateOmsOrderAsync({
          orderId,
          data: buildCreateOrderPayload(snapshot),
        })
      ).unwrap()

      const cfPayload = buildCustomFieldsPayloadForEdit(
        customFieldsFormik.values.custom_fields,
        orderFieldsMeta,
        orderCustomFieldsRaw
      )

      if (cfPayload.length) {
        try {
          await dispatch(saveOmsOrderCustomFieldsAsync({orderId, data: cfPayload})).unwrap()
        } catch (cfErr: any) {
          console.error('Order custom fields save failed', cfErr)
          toast.success('Order updated successfully.')
          toast.warning(
            cfErr?.data?.message || 'Custom fields could not be saved. You can update them later.'
          )
          await fetchOrderDetails(true)
          return
        }
      }

      toast.success('Order updated successfully.')
      await fetchOrderDetails(true)
    } catch (err: any) {
      console.error('Update order failed', err)
      toast.error(err?.data?.message || err?.message || 'Failed to update order.')
    }
  }

  // ── Cancel Order Action ────────────────────────────────────────────────────
  const handleCancelOrder = async () => {
    if (!orderId) return
    
    setShowCancelConfirm(false)
    setCancelingOrder(true)
    
    const snapshot = buildCurrentFormSnapshot({
      orderStatus: 'CANCELLED',
      orderStatusLabel: 'CANCELLED',
      orderStatusApiId: 'CANCELLED',
    })

    try {
      await dispatch(
        updateOmsOrderAsync({
          orderId,
          data: buildCreateOrderPayload(snapshot),
        })
      ).unwrap()
      
      toast.success('Order cancelled successfully.')
      fetchOrderDetails(true) // Silent reload
    } catch (err: any) {
      console.error('Cancel order failed', err)
      toast.error(err?.data?.message || err?.message || 'Failed to cancel order.')
    } finally {
      setCancelingOrder(false)
    }
  }

  // ── Invoice Actions ────────────────────────────────────────────────────────
  const handleCreateInvoice = async () => {
    setLoadingInvoice(true)
    try {
      await dispatch(createOmsOrderInvoiceAsync({ orderId: Number(orderId) })).unwrap()
      toast.success('Invoice created successfully.')
      fetchOrderDetails(true) // Silently reload the order to update the status to INVOICED
    } catch (err: any) {
      console.error('Failed to create invoice', err)
      toast.error(err?.data?.message || err?.message || 'Failed to create invoice.')
    } finally {
      setLoadingInvoice(false)
    }
  }

  const handleViewInvoice = async () => {
    if (downloadCooldown) return
    setDownloadCooldown(true)
    setTimeout(() => setDownloadCooldown(false), 5000)
    setLoadingInvoice(true)
    try {
      await dispatch(downloadOmsOrderInvoiceAsync({ orderId: Number(orderId) })).unwrap()
    } catch (err: any) {
      console.error('Failed to download invoice', err)
      toast.error(err?.data?.message || err?.message || 'Failed to download invoice.')
    } finally {
      setLoadingInvoice(false)
    }
  }

  // ── Dynamic Order-Level Allocation Status ──────────────────────────────────
  const calculatedAllocationStatus = React.useMemo(() => {
    if (apiAllocationStatus) return apiAllocationStatus

    if (lineItems.length === 0) return 'Not Allocated'

    let totalRequested = 0
    let totalAllocated = 0

    lineItems.forEach((li) => {
      totalRequested += Number(li.quantity) || 0
      totalAllocated += li.allocation_details?.net_allocated_quantity || 0
    })

    if (totalAllocated === 0) return 'Not Allocated'
    if (totalAllocated >= totalRequested) return 'Fully Allocated'
    return 'Partially Allocated'
  }, [lineItems, apiAllocationStatus])

  // ── Permissions Engine ─────────────────────────────────────────────────────
  const permissions = React.useMemo(() => {
    // Use the raw api_status (apiLifecycleStatus) 
    // This is the only reliable source for detecting the order's true lifecycle state.
    if (apiLifecycleStatus === 'CANCELLED' || apiLifecycleStatus === 'COMPLETED') {
      return { isFullyViewOnly: true, canEditWarehouseOnly: false }
    }
    
    if (isCreatedByCommercium === false) {
      const isFullyAllocated = 
        calculatedAllocationStatus === 'Fully Allocated' || 
        calculatedAllocationStatus === '✔ Fully Reserved' || 
        calculatedAllocationStatus === 'ALLOCATED'

      if (!isFullyAllocated) {
        return { isFullyViewOnly: false, canEditWarehouseOnly: true }
      }
      return { isFullyViewOnly: true, canEditWarehouseOnly: false }
    }

    return { isFullyViewOnly: false, canEditWarehouseOnly: false }
  }, [apiLifecycleStatus, isCreatedByCommercium, calculatedAllocationStatus, hasInvoice])

  const isCancelledOrCompleted = apiLifecycleStatus === 'CANCELLED' || apiLifecycleStatus === 'COMPLETED';
  const isFormDisabled = permissions.isFullyViewOnly || permissions.canEditWarehouseOnly
  const isViewOnlyBecauseAllocated = 
    permissions.isFullyViewOnly && 
    isCreatedByCommercium === false && 
    !isCancelledOrCompleted;

  // ── Synced Fields Helper ───────────────────────────────────────────────────
  const isFieldNotProvided = (val: any) => isCreatedByCommercium === false && !val
  const getSyncedWrapperProps = (val: any) => {
    if (!isFieldNotProvided(val)) return {}
    return {className: 'oms-field--not-provided'}
  }
  const getSyncedInputProps = (val: any, defaultPlaceholder?: string) => {
    if (isCreatedByCommercium !== false || !!val) return {disabled: isFormDisabled, placeholder: defaultPlaceholder}
    return {disabled: true, placeholder: 'Not provided'}
  }
  // Context-aware helper text so field tooltips explain the current state.
  const fieldStateNote = (val: any): string | null => {
    if (isFieldNotProvided(val)) return 'This field was not provided by the source platform, so it is shown as read-only.'
    if (permissions.isFullyViewOnly) return 'This order is view-only, so this field cannot be edited.'
    if (permissions.canEditWarehouseOnly) return 'This is an externally synced order. Only the fulfillment warehouse can be updated — all other fields are read-only.'
    return null
  }
  const withStateNote = (base: string, val: any): React.ReactNode => {
    const note = fieldStateNote(val)
    return note ? (
      <>
        {base}
        <br />
        <em>{note}</em>
      </>
    ) : (
      base
    )
  }
  const lockedFieldTip = permissions.isFullyViewOnly
    ? 'This order is view-only and cannot be edited'
    : permissions.canEditWarehouseOnly
    ? 'This is an externally synced order. Only the fulfillment warehouse can be updated — all other fields are read-only.'
    : null
  const handleDiscardChanges = () => {
    navigate(AppUrls.allOrders())
  }

  const wrapLockedField = (node: React.ReactElement, compact = false) =>
    lockedFieldTip ? (
      <OmsTooltip tip={lockedFieldTip} inline={!compact}>
        {node}
      </OmsTooltip>
    ) : (
      node
    )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div id='oms-create-order-root' className='omsOrderScope'>
      {pageLoading && (
        <div className='omsOrderScope__loader'>
          <div className='text-center my-2'>
            <Loader variant="section" />
          </div>
        </div>
      )}
      <div className='omsOrderScope__scroll'>

      {/* ══ STICKY HEADER ══ */}
      <div className='omsOrderScope__header'>
        <div className='flex items-center justify-between'>
        <div className='flex flex-col'>
          <PremiumBreadcrumb
            items={[
              { label: 'Sales Order', to: AppUrls.allOrders() },
              { label: `Order: ${salesOrderNumber || `#${orderId}`}` },
            ]}
          />
          <div className='font-bold text-[18px] flex items-center gap-[10px]' style={{color: 'var(--text)'}}>
            Order: {salesOrderNumber || `#${orderId}`}
            {isCancelledOrCompleted ? (
              <span className='inline-block px-2 py-[2px] bg-[#f8d7da] text-[#721c24] rounded-[4px] font-semibold text-[11px] border border-[#f5c6cb]'>
                🔒 View Only — {apiLifecycleStatus || 'Locked'}
              </span>
            ) : isViewOnlyBecauseAllocated ? (
              <span className='inline-block px-2 py-[2px] bg-[#d4edda] text-[#155724] rounded-[4px] font-semibold text-[11px] border border-[#c3e6cb]'>
                ✔ Warehouse Allocated
              </span>
            ) : isCreatedByCommercium === false ? (
              <span className='inline-block px-2 py-[2px] bg-[#fff3cd] text-[#856404] rounded-[4px] font-semibold text-[11px] border border-[#ffeeba]'>
                🏭 Warehouse Allocation Mode
              </span>
            ) : (
              <span className='inline-block px-2 py-[2px] bg-[#e1f5fe] text-[#0288d1] rounded-[4px] font-semibold text-[11px]'>
                Edit Mode
              </span>
            )}
          </div>
          {orderCreatedAt && (
            <div className='oms-hint'>Created On: {orderCreatedAt}</div>
          )}
        </div>

        <div className='flex gap-[10px] items-center'>
          {/* Logistics action buttons */}
          <div className='flex gap-[6px] border-r border-[#dee2e6] pr-[15px] mr-[5px]'>
            {shippingRoute !== 'loading' && 
              (shippingRoute === 'packages' || shippingRoute === 'open') && 
              apiLifecycleStatus !== 'CANCELLED' && (
              <OmsTooltip tip='Create a new package to group items for fulfillment'>
                <button
                  type='button'
                  onClick={() => {
                    setForceViewMode(false)
                    setInitialOpenPkgId(null)
                    setShowPackageModal(true)
                  }}
                  className='oms-btn bg-[#e3f2fd] text-[#1565c0] border border-[#bbdefb] hover:bg-[#bbdefb] hover:border-[#90caf9] transition-colors'
                >
                  📦 Create Package
                </button>
              </OmsTooltip>
            )}
            {shippingRoute !== 'loading' && 
              (shippingRoute === 'direct' || shippingRoute === 'open') && 
              apiLifecycleStatus !== 'CANCELLED' && (
              <OmsTooltip tip='Create a carrier shipment for this order'>
                <button
                  type='button'
                  onClick={() => {
                    setForceViewMode(false)
                    setInitialOpenShipmentId(null)
                    setShowShipmentModal(true)
                  }}
                  className='oms-btn bg-[#fff3e0] text-[#e65100] border border-[#ffe0b2] hover:bg-[#ffe0b2] hover:border-[#ffcc80] transition-colors'
                >
                  🚚 Create Shipment
                </button>
              </OmsTooltip>
            )}
            {/* TODO: Temporarily restricted to Native Commercium orders. Remove isCreatedByCommercium !== false check to re-enable for synced orders */}
            {isCreatedByCommercium !== false && hasInvoice && orderStatus !== 'CANCELLED' && orderStatus !== 'COMPLETED' && (
              <OmsTooltip tip='Cancel this order. This action cannot be undone.'>
                <button
                  type='button'
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={cancelingOrder}
                  className='oms-btn oms-btn--danger'
                >
                  {cancelingOrder ? (
                    <Spinner animation='border' size='sm' />
                  ) : (
                    <>🚫 Cancel Order</>
                  )}
                </button>
              </OmsTooltip>
            )}
              <OmsTooltip tip='Open the generated invoice for this order'>
                <button
                  type='button'
                  onClick={handleViewInvoice}
                  disabled={loadingInvoice || downloadCooldown}
                  className='oms-btn bg-[#f3e5f5] text-[#6a1b9a] border border-[#e1bee7] hover:bg-[#e1bee7] hover:border-[#ce93d8] transition-colors'
                >
                  {loadingInvoice ? <Spinner animation='border' size='sm' /> : '📄 View Invoice'}
                </button>
              </OmsTooltip>
            {/* TODO: Temporarily commented out Create Invoice.
              Uncomment to restore Create Invoice capability when ready:

            {!hasInvoice && (
              <button
                type='button'
                onClick={handleCreateInvoice}
                disabled={loadingInvoice}
                className='oms-btn oms-btn--light'
              >
                {loadingInvoice ? <Spinner animation='border' size='sm' /> : '📄 Create Invoice'}
              </button>
            )}
            */}
          </div>

          {!isCancelledOrCompleted && !isViewOnlyBecauseAllocated && (
            <>
              <OmsTooltip tip='Discard unsaved changes and go back'>
                <button
                  type='button'
                  onClick={handleDiscardChanges}
                  className='oms-btn oms-btn--ghost'
                >
                  Discard Changes
                </button>
              </OmsTooltip>
              
              <OmsTooltip
                tip={
                  permissions.isFullyViewOnly
                    ? 'This order is view-only and cannot be modified'
                    : 'Save your changes to this order'
                }
              >
                <button
                  type='button'
                  onClick={handleSaveOrder}
                  disabled={orderSaving || permissions.isFullyViewOnly || saveCooldown}
                  className='oms-btn oms-btn--primary'
                >
                  {orderSaving ? 'Saving…' : (isCreatedByCommercium === false ? 'Save Allocation' : 'Save Modifications')}
                </button>
              </OmsTooltip>
            </>
          )}
        </div>
        </div>
      </div>

      <div className='omsOrderScope__content'>
        {permissions.isFullyViewOnly && (
          <div className='bg-[#fff3cd] border border-[#ffeeba] text-[#856404] px-[20px] py-[15px] rounded-[6px] mb-[20px] text-[15px] flex items-center gap-[10px] shadow-sm'>
            <span className='font-bold text-[16px]'>🔒 Read-Only:</span> 
            <span>
              {isViewOnlyBecauseAllocated 
                ? 'Warehouse fully allocated for this external order - and cannot be modified.'
                : `This order is in a finalized state (${apiLifecycleStatus || 'locked'}) and cannot be modified.`}
            </span>
          </div>
        )}

        {/* ══ Section 1 · Customer Identity & Destination Information ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Customer Identity &amp; Destination Information</h2></div>
          <div className={sectionBodyCls}>

          {/* Locked customer display */}
          <div className='mb-[25px] max-w-[50%]'>
            <OmsInfoLabel
              text='Linked Customer Account Profile'
              tip='The customer account associated with this order. Set at creation and read-only here.'
            />
            <OmsTooltip tip='The customer account associated with this order. Set at creation and read-only here.' inline={false}>
              <input
                type='text'
                className={inputDisabledCls}
                value={customerLabel || (selectedCustomerId ? `Customer ID: ${selectedCustomerId}` : 'No customer linked')}
                disabled
              />
            </OmsTooltip>
          </div>

          <div className='grid grid-cols-2 gap-[20px] mb-[25px]'>
            <div>
              <OmsInfoLabel text='Email Address' tip='Contact email used for order notifications and receipts' />
              {wrapLockedField(
                <input type='email' className={inputCls} placeholder='customer@domain.com' value={email} onChange={(e) => setEmail(e.target.value)} disabled={isFormDisabled} />
              )}
            </div>
            <div>
              <OmsInfoLabel text='Phone Number' tip='Contact phone number for delivery and support' />
              {wrapLockedField(
                <input type='tel' className={inputCls} placeholder='+1 (555) 000-0000' value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isFormDisabled} />
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 gap-[20px]'>
            <OmsAddressBlock
              title='Billing Address'
              data={billing}
              onChange={(field, val) => setBilling((b) => ({...b, [field]: val}))}
              onAddressChange={(updates) => setBilling((b) => ({...b, ...updates}))}
              disabled={isFormDisabled}
              inputCls={inputCls}
            />
            <OmsAddressBlock
              title='Shipping Address'
              data={shipping}
              onChange={(field, val) => setShipping((s) => ({...s, [field]: val}))}
              onAddressChange={(updates) => setShipping((s) => ({...s, ...updates}))}
              disabled={isFormDisabled}
              inputCls={inputCls}
            />
          </div>
          </div>
        </div>

        {/* ══ Section 2 · Order Routing Parameters & Allocation Metrics ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Order Routing Parameters &amp; Allocation Metrics</h2></div>
          <div className={sectionBodyCls}>

          {/* Row 1: Order Ref, Channel Source, Lifecycle Status, Fulfillment */}
          <div className='grid grid-cols-4 gap-[20px] mb-[20px]'>
            <div>
              <OmsInfoLabel text='Order Reference ID' tip='System-generated unique identifier for this order (read-only)' />
              <input
                type='text'
                className={`${inputDisabledCls} font-bold`}
                style={{color: '#222222'}}
                value={salesOrderNumber || ''}
                disabled
              />
            </div>
            <div {...getSyncedWrapperProps(channelSource)}>
              <OmsInfoLabel
                text='Order Source'
                required
                tip={withStateNote('The sales channel this order originates from', channelSource)}
              />
              <OmsPlatformFieldDataSelect
                fieldId={resolveOrderFieldId(orderDataFieldIds.channel, 'CHANNEL')}
                value={channelSource}
                onChange={setChannelSource}
                {...getSyncedInputProps(channelSource, 'Select Order Source...')}
              />
            </div>
            <div {...getSyncedWrapperProps(orderStatus)}>
              <OmsInfoLabel
                text='Order Status'
                required
                tip={withStateNote('Current lifecycle stage of the order (e.g. Pending, Processing, Shipped)', orderStatus)}
              />
              <OmsPlatformFieldDataSelect
                fieldId={resolveOrderFieldId(orderDataFieldIds.orderStatus, 'ORDER_STATUS')}
                value={orderStatus}
                onChange={(val, label, raw) => {
                  setOrderStatus(val)
                  setOrderStatusLabel(label)
                  setOrderStatusApiId(raw?.api_id ?? null)
                }}
                seedOption={fieldSeedOptions.orderStatus ?? null}
                {...getSyncedInputProps(orderStatus, 'Select Order Status...')}
              />
            </div>
            <div>
              <OmsInfoLabel
                text='Fulfillment State'
                tip='Whether the order items have been fulfilled. Derived automatically from packages and shipments.'
              />
              {getFulfillmentBadge(fulfillmentStatus)}
            </div>
          </div>

          {/* Row 2: Allocation, Warehouse, Shipping, Shipment Date */}
          <div className='grid grid-cols-4 gap-[20px] mb-[20px]'>
            <div>
              <OmsInfoLabel
                text='Inventory Allocation Status'
                tip='How much of the ordered quantity has been reserved from inventory'
              />
              {getAllocationBadge(calculatedAllocationStatus)}
            </div>

            {/* Warehouse (LOCATION platform field) */}
            <div>
              <OmsInfoLabel
                text='Warehouse'
                required
                tip={
                  permissions.isFullyViewOnly
                    ? 'The warehouse fulfilling this order. This order is view-only, so it cannot be changed.'
                    : 'The warehouse that will fulfill this order. Determines available stock for line items.'
                }
              />
              <OmsPlatformFieldDataSelect
                fieldId={resolveOrderFieldId(orderDataFieldIds.location, 'LOCATION')}
                value={warehouseId}
                onChange={handleWarehouseChange}
                placeholder='Select Warehouse...'
                loading={orderConfigFieldsLoading}
                seedOption={fieldSeedOptions.location ?? null}
                disabled={permissions.isFullyViewOnly}
              />
            </div>

            <div {...getSyncedWrapperProps(shippingMethod)}>
              <OmsInfoLabel
                text='Shipping Carrier & Method'
                tip={withStateNote('Carrier and service level used to ship this order', shippingMethod)}
              />
              <OmsPlatformFieldDataSelect
                fieldId={resolveOrderFieldId(orderDataFieldIds.shipping, 'SHIPPINGPROVIDER')}
                value={shippingMethod}
                onChange={setShippingMethod}
                {...getSyncedInputProps(shippingMethod, 'Select Shipping Method...')}
              />
            </div>

            <div {...getSyncedWrapperProps(shipmentDate)}>
              <OmsInfoLabel
                text='Expected Shipment Date'
                tip={withStateNote('Target date this order is expected to ship', shipmentDate)}
              />
              <input type='date' className={`${inputCls} ${!shipmentDate ? 'is-empty-date' : ''}`} value={shipmentDate} onChange={(e) => setShipmentDate(e.target.value)} {...getSyncedInputProps(shipmentDate)} />
            </div>
          </div>

          {/* Row 3: Payment */}
          <div className='grid grid-cols-4 gap-[20px]'>
            <div {...getSyncedWrapperProps(paymentMethod)}>
              <OmsInfoLabel
                text='Payment Gateway / Method'
                tip={withStateNote('Payment method or gateway used to collect payment', paymentMethod)}
              />
              <OmsPlatformFieldDataSelect
                fieldId={resolveOrderFieldId(orderDataFieldIds.payment, 'PAYMENT_METHOD')}
                value={paymentMethod}
                onChange={setPaymentMethod}
                {...getSyncedInputProps(paymentMethod, 'Select Payment Method...')}
              />
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 3 · Ordered Items & Inventory Demands ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Ordered Items &amp; Inventory Demands</h2></div>
          <div className={sectionBodyCls}>


          {/* Items table */}
          <div className='w-full overflow-x-auto pb-[10px]'>
            <table className='w-full border-collapse mt-[15px] min-w-[800px]'>
            <thead>
              <tr>
                <th className={thCls} style={{width: '34%'}}>
                  <OmsInfoText className='oms-th-hint' text='Product / Variant / SKU' tip='Product variant on this order line' />
                </th>
                <th className={thCls} style={{width: '9%'}}>
                  <OmsInfoText className='oms-th-hint' text='Quantity' tip='Number of units ordered for this line item' />
                </th>
                <th className={thCls} style={{width: '13%'}}>
                  <OmsInfoText className='oms-th-hint' required text='Unit Price' tip='Price per unit before discount and tax' />
                </th>
                <th className={thCls} style={{width: '13%'}}>
                  <OmsInfoText className='oms-th-hint' text={`Line Level Discount (${currencySymbol})`} tip='Flat discount amount applied to this line item' />
                </th>
                <th className={thCls} style={{width: '11%'}}>
                  <OmsInfoText className='oms-th-hint' text='Allocation' tip='How much of this line quantity has been reserved from inventory' />
                </th>
                <th className={thCls} style={{width: '10%'}}>
                  <OmsInfoText className='oms-th-hint' text='Item Subtotal' tip='Calculated line total: (quantity × unit price) − discount' />
                </th>
                <th className={`${thCls} text-center`} style={{width: '10%'}}>
                  <OmsInfoText className='oms-th-hint' text='Actions' tip='Remove a line item from the order' />
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className='text-center text-[#6c757d] p-[30px] border-b border-[#dee2e6]'>
                    {isRefetchingInventory ? (
                       <span><Spinner animation='border' size='sm' className="mr-2" /> Loading inventory...</span>
                    ) : warehouseId ? (
                      'No line items yet. Use "+ Add Line Item" below to add products.'
                    ) : (
                      'Select a warehouse first, then add line items.'
                    )}
                  </td>
                </tr>
              ) : (
                lineItems.map((item) => (
                  <tr 
                    key={item.id}
                    style={item.id === glowingRowId ? { backgroundColor: '#fef9c3', transition: 'background-color 0.5s ease-in-out' } : { transition: 'background-color 0.5s ease-in-out' }}
                  >
                    {/* Product / variant cell */}
                    <td className={tdCls}>
                      <div style={{ width: '100%', maxWidth: '380px' }}>
                      {item.isNew ? (
                        /* New row: full variant picker */
                      <div data-variant-dropdown='true' style={{position: 'relative'}}>

                        {/* Trigger: shows search input when open, selected label when closed */}
                        {openVariantRow === item.id ? (
                          <div
                            className='flex items-center px-[14px] py-[10px] bg-white'
                            style={{ 
                              border: '1px solid #3b82f6', 
                              borderRadius: '8px',
                              minHeight: '42px',
                              boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.12)',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#3b82f6', marginRight: '10px', marginLeft: '8px', flexShrink: 0 }}>
                              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                            </svg>
                            <input
                              autoFocus
                              className='flex-1 !outline-none !border-none !shadow-none bg-transparent text-[13px] text-[#333] placeholder-[#adb5bd] p-0 focus:!ring-0 focus:!outline-none focus:!border-transparent focus:!shadow-none ring-0'
                              style={{ boxShadow: 'none', outline: 'none', border: 'none', WebkitAppearance: 'none' }}
                              placeholder='Type to search variants...'
                              value={item.variantSearch}
                              onChange={(e) => handleVariantSearch(item.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {item.variantSearch && (
                              <button
                                className='ml-2 flex items-center justify-center text-[#adb5bd] hover:text-[#495057] hover:bg-[#f1f3f5]'
                                onClick={(e) => { e.stopPropagation(); handleVariantSearch(item.id, '') }}
                                style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', transition: 'all 0.15s ease' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div
                            className='flex justify-between items-center px-[14px] py-[10px] bg-white select-none'
                            style={{
                              border: '1px solid #d1d5db', 
                              borderRadius: '8px',
                              minHeight: '42px',
                              cursor: warehouseId ? 'pointer' : 'not-allowed', 
                              opacity: warehouseId ? 1 : 0.6,
                              transition: 'border-color 0.2s ease',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                            onClick={(e) => warehouseId && openVariantDropdown(item.id, e.currentTarget)}
                            onMouseEnter={(e) => { if (warehouseId) e.currentTarget.style.borderColor = '#9ca3af' }}
                            onMouseLeave={(e) => { if (warehouseId) e.currentTarget.style.borderColor = '#d1d5db' }}
                          >
                            <div className='flex items-center overflow-hidden' style={{ flex: 1 }}>
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#adb5bd', marginRight: '10px', marginLeft: '10px', flexShrink: 0 }}>
                                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                              </svg>
                              <span style={{color: item.variantOption ? '#333333' : '#6c757d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: '13px', lineHeight: '20px'}}>
                                {item.variantOption
                                  ? item.variantOption.label
                                  : warehouseId
                                  ? 'Search and select a variant...'
                                  : 'Select warehouse first'}
                              </span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px', color: '#adb5bd', marginLeft: '8px', flexShrink: 0 }}>
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}

                        {/* Selected variant meta */}
                        {item.variantOption && (
                          <div className='flex flex-wrap items-center gap-[6px] mt-[4px]'>
                            {item.sku && (
                              <span style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                SKU: {item.sku}
                              </span>
                            )}
                            {item.variantOption.availableQty != null && (() => {
                              const style = getStockBadgeStyle(item.variantOption.availableQty)
                              const allocated = (item.variantOption as any).allocatedQty || 0
                              const whQty = (item.variantOption as any).warehouseQty ?? item.variantOption.availableQty

                              if (allocated > 0) {
                                return (
                                  <OmsTooltip tip="This order already has stock reserved for it, which counts towards the available quantity for this edit session.">
                                    <span style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'help' }}>
                                      Available: {whQty} (+{allocated} reserved)
                                    </span>
                                  </OmsTooltip>
                                )
                              }

                              return (
                                <span style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                  Available: {item.variantOption.availableQty}
                                </span>
                              )
                            })()}
                          </div>
                        )}
                        {/* INVENTORY STATUS BANNERS */}
                        {item.inventoryStatus === 'NOT_FOUND' && (
                          <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>⚠️</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>SKU Not Found</strong>
                              Remove this item or change warehouse.
                            </div>
                          </div>
                        )}
                        {item.inventoryStatus === 'OUT_OF_STOCK' && (
                          <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>🚫</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Out of Stock (0 available)</strong>
                              Remove this item or change warehouse.
                            </div>
                          </div>
                        )}
                        {item.inventoryStatus === 'INSUFFICIENT_STOCK' && (
                          <div style={{ backgroundColor: '#fffbe6', color: '#d48806', border: '1px solid #ffe58f', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                            <span style={{ fontSize: '14px', lineHeight: '1' }}>⚡</span>
                            <div>
                              <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Insufficient Stock (Only {item.variantOption?.availableQty} available)</strong>
                              Reduce quantity or change warehouse.
                            </div>
                          </div>
                        )}

                        {/* Dropdown panel */}
                        {openVariantRow === item.id && dropdownCoords && createPortal(
                          <div
                            ref={dropdownPanelRef}
                            data-variant-dropdown='true'
                            className='z-[99999]'
                            style={{
                              position: 'fixed',
                              top: `${dropdownCoords.top}px`,
                              left: `${dropdownCoords.left}px`,
                              width: `${dropdownCoords.width}px`,
                              background: '#ffffff',
                              border: '1px solid #dee2e6',
                              borderRadius: '8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
                              overflow: 'hidden',
                              willChange: 'top, left',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className='overflow-y-auto'
                              style={{ maxHeight: '260px' }}
                              onScroll={(e) => handleVariantScroll(e, item)}
                            >
                              {item.variantLoading && item.variantOptions.length === 0 && (
                                <div style={{ padding: '20px 16px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '18px' }}>⏳</span>
                                  Loading variants...
                                </div>
                              )}
                              {item.variantOptions.map((opt, idx) => {
                                const isSelected = item.variantOption?.value === opt.value
                                const lowStock = opt.availableQty != null && opt.availableQty <= 0
                                const stockStyle = opt.availableQty != null ? getStockBadgeStyle(opt.availableQty) : null
                                return (
                                  <div
                                    key={opt.value}
                                    onClick={() => selectVariant(item.id, opt)}
                                    style={{
                                      padding: '10px 14px',
                                      cursor: 'pointer',
                                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                                      borderBottom: idx < item.variantOptions.length - 1 ? '1px solid #f1f3f5' : 'none',
                                      opacity: lowStock && enforceLineItemInventory ? 0.55 : 1,
                                      transition: 'background-color 0.12s ease',
                                    }}
                                    onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f8f9fa' }}
                                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected ? '#eff6ff' : 'transparent' }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: isSelected ? '#1d4ed8' : '#212529', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {isSelected && <span style={{ marginRight: '5px', color: '#1d4ed8' }}>✓</span>}
                                          {opt.label}
                                        </div>
                                        {opt.sku && (
                                          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px', fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                                            SKU: {opt.sku}
                                          </div>
                                        )}
                                      </div>
                                      {stockStyle && (
                                        <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', backgroundColor: stockStyle.bg, border: `1px solid ${stockStyle.border}`, color: stockStyle.color, whiteSpace: 'nowrap', alignSelf: 'center' }}>
                                          {opt.availableQty} Available
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )
                              })}
                              {!item.variantLoading && item.variantOptions.length === 0 && (
                                <div style={{ padding: '24px 16px', textAlign: 'center', color: '#6c757d', fontSize: '13px' }}>
                                  <span style={{ display: 'block', marginBottom: '6px', fontSize: '22px' }}>🔍</span>
                                  No variants found
                                  {item.variantSearch && <div style={{ marginTop: '4px', fontSize: '12px', color: '#adb5bd' }}>Try a different search term</div>}
                                </div>
                              )}
                              {item.variantLoading && item.variantOptions.length > 0 && (
                                <div style={{ padding: '10px 16px', textAlign: 'center', color: '#6c757d', fontSize: '12px', borderTop: '1px solid #f1f3f5' }}>
                                  Loading more...
                                </div>
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                      ) : (
                        /* Prefilled row: static display */
                        <div>
                          <div className='font-semibold text-[13px]'>{item.name}</div>
                            <div className='flex flex-wrap items-center gap-[6px] mt-[4px]'>
                              {item.sku && (
                                <span style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', color: '#495057', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                  SKU: {item.sku}
                                </span>
                              )}
                              {item.variantOption?.availableQty != null && (() => {
                                const style = getStockBadgeStyle(item.variantOption.availableQty)
                                const allocated = (item.variantOption as any).allocatedQty || 0
                                const whQty = (item.variantOption as any).warehouseQty ?? item.variantOption.availableQty

                                if (allocated > 0) {
                                  return (
                                    <OmsTooltip tip="This order already has stock reserved for it, which counts towards the available quantity for this edit session.">
                                      <span style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'help' }}>
                                        Available: {whQty} (+{allocated} reserved)
                                      </span>
                                    </OmsTooltip>
                                  )
                                }

                                return (
                                  <span style={{ backgroundColor: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                    Available: {item.variantOption.availableQty}
                                  </span>
                                )
                              })()}
                            </div>
                        </div>
                      )}

                      {/* INVENTORY STATUS BANNERS 
                      {item.inventoryStatus === 'NOT_FOUND' && (
                        <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                          <span style={{ fontSize: '14px', lineHeight: '1' }}>⚠️</span>
                          <div>
                            <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>SKU Not Found</strong>
                            {isCreatedByCommercium === false ? 'Please select a different warehouse.' : 'Remove this item or change warehouse.'}
                          </div>
                        </div>
                      )}
                      {item.inventoryStatus === 'OUT_OF_STOCK' && (
                        <div style={{ backgroundColor: '#fff5f5', color: '#c92a2a', border: '1px solid #ffe3e3', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                          <span style={{ fontSize: '14px', lineHeight: '1' }}>🚫</span>
                          <div>
                            <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Out of Stock (0 available)</strong>
                            {isCreatedByCommercium === false ? 'Please select a different warehouse.' : 'Remove this item or change warehouse.'}
                          </div>
                        </div>
                      )}
                      {item.inventoryStatus === 'INSUFFICIENT_STOCK' && (
                        <div style={{ backgroundColor: '#fffbe6', color: '#d48806', border: '1px solid #ffe58f', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: '1.4' }}>
                          <span style={{ fontSize: '14px', lineHeight: '1' }}>⚡</span>
                          <div>
                            <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 600 }}>Insufficient Stock (Only {item.variantOption?.availableQty} available)</strong>
                            {isCreatedByCommercium === false ? 'Please select a different warehouse.' : 'Reduce quantity or change warehouse.'}
                          </div>
                        </div>
                      )}
                      */}
                      </div>
                    </td>

                    {/* Quantity */}
                    <td className={tdCls}>
                      {wrapLockedField(
                        <input
                          type='number'
                          step='any'
                          min={1}
                          max={
                            enforceLineItemInventory
                              ? item.variantOption?.availableQty ?? undefined
                              : undefined
                          }
                          value={item.quantity}
                          onChange={(e) => {
                            let val = e.target.value.replace(/^0+(?=\d)/, '')
                            if (val === '') {
                              updateLineItem(item.id, 'quantity', '')
                              return
                            }
                            const numVal = parseFloat(val) || 0
                            const maxQty = enforceLineItemInventory
                              ? item.variantOption?.availableQty
                              : null
                            if (maxQty != null && numVal > maxQty) {
                              updateLineItem(item.id, 'quantity', maxQty.toString())
                              return
                            }
                            updateLineItem(item.id, 'quantity', val)
                          }}
                          disabled={isFormDisabled || (enforceLineItemInventory && item.inventoryStatus === 'NOT_FOUND')}
                          className={`${inputSmCls} w-full`}
                        />
                      )}
                    </td>

                    {/* Unit price */}
                    <td className={tdCls}>
                      {wrapLockedField(
                        <input
                          type='number'
                          step='any'
                          value={item.price}
                          onChange={(e) => updateLineItem(item.id, 'price', e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                          disabled={isFormDisabled}
                          className={`${inputSmCls} w-full ${item.price === null || item.price === undefined || item.price === '' as any ? 'border-danger' : ''}`}
                        />
                      )}
                    </td>

                    {/* Discount */}
                    <td className={tdCls}>
                      {wrapLockedField(
                        <input
                          type='number'
                          step='any'
                          value={item.discount}
                          onChange={(e) => updateLineItem(item.id, 'discount', e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                          disabled={isFormDisabled}
                          className={`${inputSmCls} w-full`}
                        />
                      )}
                    </td>

                    {/* Allocation Pill */}
                    <td className={tdCls}>
                      <AllocationPill 
                        allocation={item.allocation_details} 
                        allocationHistory={item.allocation_history}
                        currentQuantity={Number(item.quantity) || 0} 
                        productName={item.name || (item.variantOption?.label ?? '')}
                        productSku={item.sku || (item.variantOption?.sku ?? '')}
                      />
                    </td>

                    {/* Subtotal */}
                    <td className={`${tdCls} font-semibold`}>
                      {currencySymbol}{(Number(getLineSubtotal(item)) || 0).toFixed(2)}
                    </td>

                    {/* Remove */}
                    <td className={`${tdCls} text-center`}>
                      {!isFormDisabled && (
                        <OmsTooltip tip='Remove this line item from the order'>
                          <button
                            type='button'
                            onClick={() => removeLineItem(item.id)}
                            className='px-[10px] py-1 rounded-[4px] text-[13px] font-medium cursor-pointer inline-flex items-center gap-[6px] bg-[#fff5f5] border border-[#f5c6cb] text-[#dc3545] hover:bg-[#f8d7da]'
                          >
                            🗑 Delete
                          </button>
                        </OmsTooltip>
                      )}
                    </td>
                  </tr>
                ))
              )}
              {!isFormDisabled && (
                <tr>
                  <td className={`${tdCls} pt-4`} colSpan={7}>
                    <div className='flex items-center gap-3'>
                      <OmsTooltip
                        tip={
                          warehouseId
                            ? 'Search and add another variant from the selected warehouse'
                            : 'Select a warehouse first to add line items'
                        }
                      >
                        <button
                          type='button'
                          onClick={addLineItem}
                          disabled={!warehouseId}
                          className='oms-btn oms-btn--primary'
                        >
                          + Add Line Item
                        </button>
                      </OmsTooltip>
                      {!warehouseId && (
                        <span className='text-[12px] text-[#6c757d]'>Select a warehouse above first</span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {/* ── Totals block ── */}
          <div className='ml-auto mt-[20px] flex flex-col gap-[8px]' style={{ width: '440px' }}>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Shipping Fee' tip={withStateNote('Additional shipping charge added to the order total', shippingFee)} className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                {wrapLockedField(
                  <input
                    type='number'
                    step='any'
                    value={shippingFee}
                    onChange={(e) => setShippingFee(e.target.value === '' ? '' : e.target.value.replace(/^0+(?=\d)/, ''))}
                    disabled={isFormDisabled}
                    className={`${inputSmCls} w-[120px] text-right shrink-0`}
                  />,
                  true
                )}
              </div>
            </div>
            {isCreatedByCommercium !== false && (
              <div className='flex justify-between items-center text-[14px] gap-[16px]'>
                <OmsInfoText
                  text='Tax Percent (%)'
                  tip={withStateNote('Tax percentage applied to item subtotal plus shipping', taxPercent != null ? `${taxPercent}%` : '')}
                  className='whitespace-nowrap'
                />
                <div className='w-[200px] flex justify-end items-center gap-1.5'>
                  {wrapLockedField(
                    <div className='inline-flex items-center gap-1.5 justify-end shrink-0'>
                      <input
                        type='number'
                        step='any'
                        min={0}
                        value={taxPercent === null ? '' : taxPercent}
                        onChange={(e) => {
                          const val = e.target.value
                          if (val === '') {
                            setTaxPercent(null)
                          } else {
                            const num = parseFloat(val)
                            setTaxPercent(isNaN(num) ? null : Math.max(0, num))
                          }
                        }}
                        disabled={isFormDisabled}
                        placeholder='e.g. 10'
                        className={`${inputSmCls} w-[120px] text-right shrink-0`}
                      />
                      <span className='text-[14px] text-gray-800 font-bold ms-1 select-none'>%</span>
                    </div>,
                    true
                  )}
                </div>
              </div>
            )}
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText
                text={`Tax Amount (${currencySymbol})`}
                tip={withStateNote(
                  isCreatedByCommercium === false
                    ? 'Tax amount recorded on the source platform order'
                    : 'Calculated from tax percent applied to item subtotal plus shipping',
                  calculatedTax
                )}
                className='whitespace-nowrap'
              />
              <div className='w-[200px] flex justify-end'>
                {wrapLockedField(
                  <span
                    className={`inline-flex shrink-0${isFieldNotProvided(calculatedTax) ? ' oms-field--not-provided' : ''}`}
                  >
                    <input
                      type='text'
                      readOnly
                      disabled
                      value={isFieldNotProvided(calculatedTax) ? '' : calculatedTax.toFixed(2)}
                      placeholder={isFieldNotProvided(calculatedTax) ? 'Not provided' : undefined}
                      className={`${inputSmCls} w-[120px] shrink-0 ${isFieldNotProvided(calculatedTax) ? 'text-left' : 'text-right'}`}
                      tabIndex={-1}
                      aria-readonly
                    />
                  </span>,
                  true
                )}
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Order Discount' tip='Order-level discount applied to line subtotal plus shipping. Choose a flat amount or percentage.' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end items-center gap-2'>
                {wrapLockedField(
                  <select
                    value={orderDiscountMode}
                    onChange={(e) => setOrderDiscountMode(e.target.value as OrderDiscountMode)}
                    disabled={isFormDisabled}
                    className={`${inputSmCls} w-[52px] shrink-0`}
                  >
                    <option value='amount'>{currencySymbol}</option>
                    <option value='percent'>%</option>
                  </select>,
                  true
                )}
                {wrapLockedField(
                  <input
                    type='number'
                    step='any'
                    min={0}
                    max={orderDiscountMode === 'percent' ? 100 : undefined}
                    value={orderDiscountValue}
                    placeholder={orderDiscountMode === 'percent' ? 'e.g. 2' : 'e.g. 100'}
                    disabled={isFormDisabled}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/^0+(?=\d)/, '')
                      if (raw === '') {
                        setOrderDiscountValue('')
                        return
                      }
                      const numVal = parseFloat(raw) || 0
                      if (numVal < 0) {
                        setOrderDiscountValue('0')
                      } else if (orderDiscountMode === 'percent' && numVal > 100) {
                        setOrderDiscountValue('100')
                      } else {
                        setOrderDiscountValue(raw)
                      }
                    }}
                    className={`${inputSmCls} w-[120px] text-right shrink-0`}
                  />,
                  true
                )}
              </div>
            </div>
            <div className='flex justify-between py-[4px] items-center text-[14px] font-semibold border-t border-[#dee2e6] mt-[4px] gap-[16px]'>
              <OmsInfoText text='Estimated Grand Total' tip='Grand Total including tax and Shipping fees' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <span>{currencySymbol}{grandTotal.toFixed(2)}</span>
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] text-[#28a745] font-semibold gap-[16px]'>
              <OmsInfoText text={`Amount Paid (${currencySymbol})`} tip={withStateNote('Total amount received for this order', amountPaid)} className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                {wrapLockedField(
                  <input
                    type='number'
                    step='any'
                    min={0}
                    value={amountPaid}
                    onChange={(e) => {
                      let val = e.target.value.replace(/^0+(?=\d)/, '')
                      if (val === '') {
                        setAmountPaid('')
                        return
                      }
                      const numVal = parseFloat(val) || 0
                      if (numVal < 0) {
                        setAmountPaid('0')
                      } else if (numVal > grandTotal) {
                        setAmountPaid(grandTotal.toString())
                      } else {
                        setAmountPaid(val)
                      }
                    }}
                    disabled={isFormDisabled}
                    className={`${inputSmCls} w-[120px] text-right shrink-0`}
                    style={{borderColor: 'rgba(40, 167, 69, 0.45)'}}
                  />,
                  true
                )}
              </div>
            </div>
            <div className='flex justify-between items-center text-[14px] gap-[16px]'>
              <OmsInfoText text='Payment Reference / Txn ID' tip={withStateNote('External payment transaction or check reference number', paymentRef)} className='text-[12px] text-[#6c757d] whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                {wrapLockedField(
                  <input
                    type='text'
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder='e.g. TXN-98421, Check #104'
                    disabled={isFormDisabled}
                    className={`${inputSmCls} w-[170px]`}
                  />
                )}
              </div>
            </div>
            <hr className='border-0 border-t border-[#dee2e6] my-1' />
            <div className='flex justify-between items-center font-bold text-[16px] text-[#007bff] gap-[16px]'>
              <OmsInfoText text='Balance Due' tip='The remaining amount to be paid' className='whitespace-nowrap' />
              <div className='w-[200px] flex justify-end'>
                <span>{currencySymbol}{balanceDue.toFixed(2)}</span>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 4 · Shipments ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Shipments</h2></div>
          <div className={sectionBodyCls}>

          <div className='w-full overflow-x-auto pb-[10px]'>
            <table className='w-full border-collapse min-w-[800px]' style={{marginTop: 0}}>
            <thead>
              <tr>
                <th className={thCls}>Tracking Number</th>
                <th className={thCls}>Items Packed</th>
                <th className={thCls}>Carrier / Method</th>
                <th className={thCls}>Shipment Status</th>
                <th className={thCls} style={{ textAlign: 'center' }}>Action Log</th>
              </tr>
            </thead>
            <tbody>
              {packages.length === 0 ? (
                <tr>
                  <td colSpan={5} className='text-center text-[#6c757d] p-[20px] border-b border-[#dee2e6]'>
                    No packages or labels have been recorded for this order yet.
                  </td>
                </tr>
              ) : (
                packages.map((pkg: any, idx: number) => (
                  <tr key={pkg.id || idx}>
                    <td className={tdCls}>
                      <span style={{ backgroundColor: '#fce8f3', color: '#e83e8c', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, display: 'inline-block' }}>
                        {pkg.tracking_number || pkg.label_reference || '—'}
                      </span>
                    </td>
                    <td className={`${tdCls} text-[13px]`}>
                      {pkg.line_items && Array.isArray(pkg.line_items)
                        ? pkg.line_items.reduce((sum: number, item: any) => sum + (Number(item.quantity) || 0), 0)
                        : (pkg.items_packed || pkg.items || '—')}
                    </td>
                    <td className={tdCls}>
                      {pkg.shipping_carrier || pkg.carrier || pkg.shipping_method || '—'}
                    </td>
                    <td className={tdCls}>
                      <span className='inline-block px-2 py-[3px] bg-[#d4edda] text-[#155724] rounded-[4px] font-semibold text-[11px]'>
                        {pkg.sync_status || pkg.status || 'NA'}
                      </span>
                    </td>
                    <td className={`${tdCls} text-center`}>
                      <div className='inline-flex items-center justify-center gap-[10px]'>
                        {pkg.tracking_url && (
                          <a
                            href={pkg.tracking_url}
                            target='_blank'
                            rel='noreferrer'
                            className='oms-btn oms-btn--sm flex items-center justify-center gap-[6px] bg-[#e6f2ff] text-[#0056b3] border border-[#b8daff] hover:bg-[#cce5ff] transition-colors'
                            style={{ textDecoration: 'none' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                            Track Online
                          </a>
                        )}
                        <OmsTooltip tip='View package / shipment details'>
                          <button
                            type='button'
                            onClick={() => {
                              const id = pkg.id || pkg.package_id || pkg.shipment_id
                              setForceViewMode(true)
                              if (shippingRoute === 'packages') {
                                setInitialOpenPkgId(id)
                                setShowPackageModal(true)
                              } else if (shippingRoute === 'direct') {
                                setInitialOpenShipmentId(id)
                                setShowShipmentModal(true)
                              } else {
                                // Fallback if route is open
                                if (pkg.items_packed !== undefined || pkg.package_id !== undefined) {
                                  setInitialOpenPkgId(id)
                                  setShowPackageModal(true)
                                } else {
                                  setInitialOpenShipmentId(id)
                                  setShowShipmentModal(true)
                                }
                              }
                            }}
                            className='oms-btn oms-btn--sm flex items-center justify-center gap-[6px] bg-[#f8f9fa] text-[#495057] border border-[#dee2e6] hover:bg-[#e2e6ea] transition-colors'
                          >
                            👁 View
                          </button>
                        </OmsTooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          </div>
        </div>

        {/* ══ Section 5 · Order Notes & Documentation Log ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Order Notes &amp; Documentation Log</h2></div>
          <div className={sectionBodyCls}>
          <div className='grid grid-cols-2 gap-[20px]'>
            <div>
              <OmsInfoLabel
                text='Internal Comments (Invisible to Customer)'
                tip='Private operational notes. Never shown to the customer or on documents.'
              />
              <textarea
                className={`${inputCls} resize-y min-h-[80px] disabled:bg-gray-100 disabled:text-gray-500`}
                placeholder='Add confidential operational details, approval logs, backorder conditions, or picker instructions here...'
                value={internalComments}
                onChange={(e) => setInternalComments(e.target.value)}
                disabled={isFormDisabled}
              />
            </div>
            <div>
              <OmsInfoLabel
                text='Customer Notes (Visible on Invoice/Receipt)'
                tip='Notes shown to the customer on the invoice and receipt documents.'
              />
              <textarea
                className={`${inputCls} resize-y min-h-[80px] disabled:bg-gray-100 disabled:text-gray-500`}
                placeholder='No notes appended for customer documentation view.'
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                disabled={isFormDisabled}
              />
            </div>
          </div>
          </div>
        </div>

        {/* ══ Section 6 · Custom Fields ══ */}
        <div className={sectionCardCls}>
          <div className='oms-card__header'><h2 className='oms-card__title'>Custom Fields</h2></div>
          <div className={sectionBodyCls}>
          <OrderCustomFields
            values={customFieldsFormik.values}
            setFormikState={customFieldsFormik.setFormikState}
            mode='edit'
            onFieldsMetaLoaded={setOrderFieldsMeta}
            orderCustomFieldsRaw={orderCustomFieldsRaw}
            disabled={isFormDisabled}
            isSyncedOrder={isCreatedByCommercium === false}
            disabledTip={lockedFieldTip || undefined}
          />
          </div>
        </div>
      </div>{/* omsOrderScope__content */}
      </div>{/* omsOrderScope__scroll */}

      {/* ── Logistics Modals ── */}
      {showPackageModal && (
        <ManagePackagesModal
          show={showPackageModal}
          onClose={() => {
            setShowPackageModal(false)
            setInitialOpenPkgId(null)
            fetchOrderDetails(true) // Silent reload to sync data
          }}
          orderId={Number(orderId)}
          orderCode={salesOrderNumber || ''}
          initialOpenId={initialOpenPkgId}
          forceViewMode={forceViewMode}
        />
      )}

      {showShipmentModal && (
        <ManageShipmentsModal
          show={showShipmentModal}
          onClose={() => {
            setShowShipmentModal(false)
            setInitialOpenShipmentId(null)
            fetchOrderDetails(true) // Silent reload to sync data
          }}
          orderId={Number(orderId)}
          orderCode={salesOrderNumber || ''}
          initialOpenId={initialOpenShipmentId}
          forceViewMode={forceViewMode}
        />
      )}

      <CommonConfirmationModal
        show={showCancelConfirm}
        title="Cancel Order"
        message="Are you sure you want to cancel this order? This action cannot be undone."
        confirmText="Yes, Cancel Order"
        confirmVariant="danger"
        onConfirm={handleCancelOrder}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  )
}

export default EditOmsOrder
